import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ColDef, ColGroupDef } from 'ag-grid-community';
import { ApplicationSchemeGap } from 'src/app/environmental-exposure-assessment/eea-output/eea-output-project/eea-output-interfaces/eea-out-put-gap-interface';
import { Project } from 'src/app/shared/models/project';
import { EEAGapSurfaceWaterColdef } from '../../eea-gap-surfacewater/eea-gap-surfacewater.coldef';
import { GridComponent } from 'src/app/shared/components/grid/grid.component';
import Swal from 'sweetalert2';
import { ApplicationSchemeXApplicationWindow } from 'src/app/shared/models/application-scheme';
import { Constants } from 'src/app/shared/utils/constants';
import { MitigationsXApplicationScheme } from 'src/app/shared/models/mitigation-x-application-scheme';

@Component({
  selector: 'eea-mitigation-x-application-scheme-dialog',
  templateUrl: './eea-mitigation-x-application-scheme-dialog.component.html',
  styleUrls: ['./eea-mitigation-x-application-scheme-dialog.component.css']
})
export class EEAMitigationXApplicationSchemeDialogComponent {
  @Input() isProjectOwnershipValid: boolean = false;
  @Input() compartment!: string;
  @Input() selectedProject?: Project;
  @Input() isInverseModeling: boolean = false;
  @Input() appScheme!: ApplicationSchemeGap;
  @Output() onClickSaveButtonEvent = new EventEmitter<any>();
  @Output() public isValid = new EventEmitter<boolean>();
  @ViewChild('grid') grid!: GridComponent;
  widthSize: string = '86vw';
  visible: boolean = false;
  columsDef: (ColDef | ColGroupDef)[] = [];
  rowHeight: number = 80;
  parentRowNode: any;
  rowData!: any;
  initialData: any;
  disabled: boolean = false;
  dataSaved: boolean = false;

  constructor(private gapSurfaceWaterColdef: EEAGapSurfaceWaterColdef) { }

  showMitigationxApplicationSchemeDialog(parentRowNode: any) {
    this.parentRowNode = parentRowNode;
    this.initialData = JSON.parse(JSON.stringify(this.parentRowNode.data.mitigationsXApplicationScheme?? []));
    this.rowData = this.parentRowNode?.data?.mitigationsXApplicationScheme ?? [];
    this.setWidthSize();
    const scenariosForMitigations = this.getScenariosForMitigations(this.parentRowNode?.data?.applicationSchemeXApplicationWindow ?? []);
    this.cleanUnusedFields(this.rowData, scenariosForMitigations);
    this.columsDef = this.gapSurfaceWaterColdef.getMitigationXApplicationSchemeColumnsDefinition(this.isProjectOwnershipValid, this, scenariosForMitigations);
    this.dataSaved = false;
    this.visible = true;
  }

  cleanUnusedFields(rowData: any, scenariosForMitigations: string[]) {
    rowData?.forEach((row: MitigationsXApplicationScheme) => {
      if (!scenariosForMitigations.includes(Constants.FIELD_NAMES.SPRAY_DRIFT)) {
        row.sprayDriftBuffer = undefined;
        row.nozzleReduction = undefined;
        row.pondMassLoading = undefined;
        row.ditchMassLoading = undefined;
        row.streamMassLoading = undefined;
        row.sprayDriftOption = undefined;
      }
      if (!scenariosForMitigations.includes(Constants.FIELD_NAMES.RUN_OFF)) {
        row.fractionalReductionErosionFlux = undefined;
        row.fractionalReductionErosionMass = undefined;
        row.fractionalReductionRunoffFlux = undefined;
        row.fractionalReductionRunoffVolume = undefined;
        row.bufferZone = undefined;
      }
      if(!scenariosForMitigations.includes(Constants.FIELD_NAMES.DEPOSITION)) {
        row.distance = undefined;
      }
    });
  }

  newRowAdded(newItems: any[]) {
    const itemsToProcess = Array.isArray(newItems) ? newItems : [newItems];
    itemsToProcess.forEach(item => {
      if (!item.isDuplicate) {
        this.grid.enableControls(false);
      }
    });
  }

  showDialog(element: any, action: string, params?: any): void {
    Swal.fire({
      title: element.title,
      text: element.content,
      showCancelButton: true,
      confirmButtonColor: '#0069be',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    }).then((result) => {
      if (result.value) {
        if (action === 'delete') {
          this.removeRow(params);
        }
      }
    });
  }

  removeRow(event: any) {
    this.rowData = this.rowData.filter((row: any) => row.mitigationName !== event.data.mitigationName);
    this.grid.gridApi.refreshCells();
    this.grid.gridApi.redrawRows();
  }

  onSelectionChanged(event: any) {
    this.validatesprayDriftOptionValue();
    this.grid.gridApi.refreshCells();
    this.grid.gridApi.redrawRows();
  };

  validatesprayDriftOptionValue() {
    this.rowData.forEach((row: any) => {
      if (row.sprayDriftOption && (row.sprayDriftOption.includes('SDO_01') || row.sprayDriftOption.includes('SDO_03'))) {
        row.sprayDriftBuffer = undefined;
      }

      if (row.sprayDriftOption && (row.sprayDriftOption.includes('SDO_01') || row.sprayDriftOption.includes('SDO_02'))) {
        row.pondMassLoading = undefined;
        row.ditchMassLoading = undefined;
        row.streamMassLoading = undefined;
      }
      
      if (row.sprayDriftOption && row.sprayDriftOption.includes('SDO_03')) {
        row.nozzleReduction = 0;
      }
    });
  }

  getScenariosForMitigations(dataArray: ApplicationSchemeXApplicationWindow[]): string[] {
    const valuesToUse = [];
    let hasSprayDrift = false;
    let hasRunOff = false;
    let hasDeposition = false;

    for (const item of dataArray) {
      if (item.sprayDrift) {
        hasSprayDrift = true;
      }
      if (item.runOff) {
        hasRunOff = true;
      }
      if (item.deposition) {
        hasDeposition = true;
      }
      if (hasSprayDrift && hasRunOff && hasDeposition) {
        break;
      }
    }

    if (hasSprayDrift) {
      valuesToUse.push(Constants.FIELD_NAMES.SPRAY_DRIFT);
    }
    if (hasRunOff) {
      valuesToUse.push(Constants.FIELD_NAMES.RUN_OFF);
    }
    if (hasDeposition) {
      valuesToUse.push(Constants.FIELD_NAMES.DEPOSITION);
    }

    return valuesToUse;
  }

  save() {
    this.onClickSaveButtonEvent.emit({ mitigationsXApplicationScheme: this.rowData, parentRowNode: this.parentRowNode });
    this.dataSaved = true
    this.visible = false;
  }

  setWidthSize() {
    if (window.devicePixelRatio > 1)
      this.widthSize = '100vw';
  }

  cancel() {
    if (!this.dataSaved) {
      this.parentRowNode.data.mitigationsXApplicationScheme = this.initialData;
      this.visible = false;
    }
  }

  public enableControls(enable: boolean): void {
    this.isValid.emit(enable);
    this.grid.useAddAction = enable;
    this.disabled = !enable;
  }
}
